import React, { ReactNode, useState } from "react";
import { FormikHelpers, useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "next-i18next";
import { Button, InputText } from "@precomposer/ui";
import { SignUpCustomerData, useCustomer } from "@precomposer/data";

const accountSignUpSchema = Yup.object().shape({
  firstName: Yup.string()
    .matches(
      /^([A-Za-z\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*)$/gi,
      "account:sign-up.form.msg.first-name"
    )
    .max(40)
    .required("account:sign-up.form.error.first-name"),
  lastName: Yup.string()
    .matches(
      /^([A-Za-z\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*)$/gi,
      "account:sign-up.form.msg.last-name"
    )
    .max(40)
    .required("account:sign-up.form.error.last-name"),
  password: Yup.string()
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()\-_=+\\\|\[\]{};:'",.<>/?])[^\s]{8,}$/,
      "account:sign-up.form.msg.password"
    )
    .required("account:sign-up.form.error.password"),
  email: Yup.string()
    .email("account:sign-up.form.msg.email")
    .required("account:sign-up.form.error.email"),
});

const accountSignUpFields: SignUpCustomerData = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
};

const AccountSignUpForm = () => {
  const { t } = useTranslation(["account"]);
  const [message, setMessage] = useState<ReactNode | null>(null);
  const { signUpCustomerAction } = useCustomer();

  const { errors, values, touched, handleChange, handleSubmit, isSubmitting } =
    useFormik({
      initialValues: accountSignUpFields,
      validationSchema: accountSignUpSchema,
      onSubmit: submitForm,
    });

  async function submitForm(
    values: SignUpCustomerData,
    { resetForm, setSubmitting }: FormikHelpers<SignUpCustomerData>
  ) {
    const response = await signUpCustomerAction(values);
    setSubmitting(false);
    if (response) {
      setMessage(
        <div className={"mb-2 text-green-700"}>
          {t("account:sign-up.form.msg.response")}
        </div>
      );
      resetForm({ values: accountSignUpFields });
    } else {
      setMessage(
        <div className={"mb-2 text-red-700"}>
          {t("account:sign-up.form.error.response")}
        </div>
      );
    }
  }

  return (
    <form onSubmit={handleSubmit} className={"mb-6"}>
      <div className={"mb-6 grid gap-4 lg:grid-cols-2"}>
        <div>
          <InputText
            type={"text"}
            id={"first-name"}
            name={"firstName"}
            label={t("account:sign-up.form.label.first-name")}
            fullWidth
            value={values.firstName}
            onChange={handleChange}
            error={touched.firstName && Boolean(errors.firstName)}
            message={
              touched.firstName &&
              errors.firstName !== undefined &&
              t(`${errors.firstName}` as unknown as TemplateStringsArray)
            }
          />
        </div>
        <div>
          <InputText
            type={"text"}
            id={"last-name"}
            name={"lastName"}
            label={t("account:sign-up.form.label.last-name")}
            fullWidth
            value={values.lastName}
            onChange={handleChange}
            error={touched.lastName && Boolean(errors.lastName)}
            message={
              touched.lastName &&
              errors.lastName !== undefined &&
              t(`${errors.lastName}` as unknown as TemplateStringsArray)
            }
          />
        </div>
        <div>
          <InputText
            type={"email"}
            id={"email"}
            name={"email"}
            placeholder={"email@example.com"}
            label={t("account:sign-up.form.label.email")}
            fullWidth
            value={values.email}
            onChange={handleChange}
            error={touched.email && Boolean(errors.email)}
            message={
              touched.email &&
              errors.email !== undefined &&
              t(`${errors.email}` as unknown as TemplateStringsArray)
            }
          />
        </div>
        <div>
          <InputText
            type={"password"}
            id={"password"}
            name={"password"}
            placeholder={
              "Minimum 8 characters. Must include at least one lower case and one upper case letter, one number and one special character. No empty spaces."
            }
            label={t("account:sign-up.form.label.password")}
            fullWidth
            value={values.password}
            onChange={handleChange}
            error={touched.password && Boolean(errors.password)}
            message={
              touched.password &&
              errors.password !== undefined &&
              t(`${errors.password}` as unknown as TemplateStringsArray)
            }
            autoComplete={"new-password"}
          />
        </div>
        {message}
      </div>
      <Button type={"submit"} variant={"primary"} disabled={isSubmitting}>
        {t("account:sign-up.form.submit")}
      </Button>
    </form>
  );
};

export default AccountSignUpForm;
